import { getLangs } from '../util';
import { sortNumbers } from '../util';
import { convertPlatfromsIdsToStr } from '../util';

/**
 * @typedef {Object} DeviceTokenInfo
 * @property {number} playerId
 * @property {string} deviceToken
 * @property {number} platform
 * @property {number} utcDelta
 * @property {number} lang
 * @property {number} clientVersionCode
 */

//-----------------------------------------------------------------------------

export function isAutoPush(push) {
  const info = push.info;
  if(info === undefined || info === null) {
    return false;
  }

  const AUTO_PUSH_MARKER = '#';
  return info.startsWith(AUTO_PUSH_MARKER);
}

export function isUserPush(push) {
  return !isAutoPush(push);
}

//-----------------------------------------------------------------------------

/**
 * @typedef {number} PushStatus
 */

/** @type {PushStatus} */
export const PUSH_STATUS_INACTIVE = 0;

/** @type {PushStatus} */
export const PUSH_STATUS_ACTIVE   = 1;

/** @type {PushStatus} */
export const PUSH_STATUS_INCOMING = 2;

/** @type {PushStatus} */
export const PUSH_STATUS_FINISHED = 3;

//-----------------------------------------------------------------------------

export function getPushStatus(push) {
  if(isPushFinished(push)) {
    return PUSH_STATUS_FINISHED;
  }
  return push.status;
}

export function isPushIncoming(push) {
  const status = getPushStatus(push);
  return status === PUSH_STATUS_INCOMING;
}

export function isPushActive(push) {
  const status = getPushStatus(push);
  return status === PUSH_STATUS_ACTIVE;
}

/**
 * @param {PushObject} push
 * @returns {boolean}
 */
export function isPushFinished(push) {
  if(push.status === PUSH_STATUS_INCOMING) {
    return false;
  }
  return push.total === (push.sent + push.fails);
}

/**
 * @param {PushStatus} status
 * @returns {string}
 */
export function getPushStatusName(status) {
  switch(status) {
    case PUSH_STATUS_INACTIVE: return 'inactive';
    case PUSH_STATUS_ACTIVE:   return 'active';
    case PUSH_STATUS_INCOMING: return 'incoming';
    case PUSH_STATUS_FINISHED: return 'finished';
  }
  return `Unknown status code ${status}.`;
}

export function getPushStatusClass(push) {
  return {
    activePush: isPushActive(push),
  };
}

export function getPushDonePercent(push) {
  const done = push.sent + push.fails;
  return Math.floor(done / push.total * 100);
}

export function getPushDonePercentStr(push) {
  const donePercent = getPushDonePercent(push);
  return donePercent.toString() + '%';
}

export function canStartPush(push) {
  return push.status === PUSH_STATUS_INACTIVE && !isPushFinished(push);
}

export function canPausePush(push) {
  return push.status === PUSH_STATUS_ACTIVE && !isPushFinished(push);
}

export function createEmptyTextVariants() {
  const textVariants = [];

  for(const lang of getLangs()) {
    const textVariant = createEmptyTextVariant(lang);
    textVariants.push(textVariant);
  }

  return textVariants;
}

export function createEmptyTextVariant(lang) {
  return createTextVariant(lang, '', '');
}

export function createTextVariant(lang, title, message) {
  return {
    lang,
    title,
    message,
  };
}

export function createTextVariantsObject(textVariantInputs) {
  const textVariantsObject = {};

  for(const textVariantInput of textVariantInputs) {
    const lang    = textVariantInput.getLang();
    const title   = textVariantInput.getTitle();
    const message = textVariantInput.getMessage();

    const info = {
      title,
      message,
    };
    textVariantsObject[lang] = info;
  }

  return textVariantsObject;
}

export function createTextVariants(textVariantInputs) {
  const textVariants = [];

  for(const textVariantInput of textVariantInputs) {
    const info = {
      lang:    textVariantInput.getLang(),
      title:   textVariantInput.getTitle(),
      message: textVariantInput.getMessage(),
    };
    textVariants.push(info);
  }

  return textVariants;
}

export function hasPushInfo(push) {
  if(!('info' in push)) {
    return false;
  }
  const info = push.info;
  if(info === null) {
    return false;
  }
  return info.length > 0;
}

/**
 * @param {PushObject} push
 * @returns {boolean}
 */
export function hasPushLabel(push) {
  if(!('label' in push)) {
    return false;
  }
  const label = push.label;
  if(label === undefined || label === null) {
    return false;
  }
  return label.length > 0;
}

export function hasPushMaxVersion(push) {
  if(!('maxVersion' in push)) {
    return false;
  }
  const maxVersion = push.maxVersion;
  if(maxVersion === null) {
    return false;
  }
  return maxVersion.length > 0;
}

export function getPushMaxVersionStr(push) {
  if(!hasPushMaxVersion(push)) {
    return 'Not limited by max version';
  }
  return push.maxVersion;
}

// Property platforms is a list of platforms ids (Array).
export function hasPushPlatforms(push) {
  if(!('platforms' in push)) {
    return false;
  }
  const platforms = push.platforms;
  if(platforms === null) {
    return false;
  }
  return platforms.length > 0;
}

/**
 * @param {PushObject} push
 * @returns {boolean}
 */
export function hasPushImage(push) {
  if(!('imageUrl' in push)) {
    return false;
  }
  const imageUrl = push.imageUrl;
  if(imageUrl === undefined || imageUrl === null) {
    return false;
  }
  return imageUrl.length > 0;
}

export function getPushPlatformsStr(push) {
  if(!hasPushPlatforms(push)) {
    return 'Not limited by platform';
  }

  // Do not change original array. Sort a copy.
  const platforms = push.platforms.slice();
  sortNumbers(platforms);

  return convertPlatfromsIdsToStr(platforms);
}

// Property playersIds is a list of players ids (Array).
export function hasPushPlayersIds(push) {
  if(!('playersIds' in push)) {
    return false;
  }
  const playersIds = push.playersIds;
  if(playersIds === null) {
    return false;
  }
  return playersIds.length > 0;
}

export function getCompactJobs(jobs) {
  if(jobs === null) {
    return null;
  }

  const compacted = [];
  for(const job of jobs) {

    var foundIdx = -1;
    for(var j = 0; j < compacted.length; j++) {
      if(compacted[j].language == job.language) {
        foundIdx = j;
        break;
      }
    }

    if(foundIdx == -1) {
      const compact = {
        title:    job.title, 
        message:  job.message, 
        language: job.language, 
        total:    job.total, 
        sent:     job.sent, 
        fails:    job.fails
      }
      compacted.push(compact)
    } else {
      const compact = compacted[foundIdx]
      compact.total += job.total
      compact.sent  += job.sent
      compact.fails += job.fails
    }
  }
  return compacted;
}

export function hasPushStatInfo(push) {
  const status = getPushStatus(push);

  switch(status) {
    case PUSH_STATUS_INACTIVE:
    case PUSH_STATUS_ACTIVE:
    case PUSH_STATUS_FINISHED:
      return true;
  }
  return false;
}

export function isJobDone(job) {
  const done = job.sent + job.fails;
  return done === job.total;
}

export function getJobStateStr(job) {
  if(isJobDone(job)) {
    return 'done';
  }
  return 'not done';
}

/**
 * @typedef {Object} PushPreviewObject
 * @property {number} id
 * @property {number} ctime
 * @property {string} [info]
 * @property {string} title
 * @property {string} message
 * @property {number[]} [playersIds]
 * @property {number} total
 * @property {number} sent
 * @property {number} fails
 */

/**
 * @typedef {Object} PushObject
 * @property {number} id
 * @property {number} ctime
 * @property {number} stime
 * @property {number} srtime
 * @property {number} ttl
 * @property {string} [info]
 * @property {string} label
 * @property {string} imageUrl
 * @property {string} title
 * @property {string} message
 * @property {number[]} [playersIds]
 * @property {number} total
 * @property {number} sent
 * @property {number} fails
 */
